import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Descriptions,
  Spin,
} from 'antd';
import {
  SaveOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { BillingService } from '../../services/api/billingService';
import { BillingAccount, BillingAccountUpdate, BillingStatus } from '../../types/billing';
import { getApiErrorMessage } from '../../utils/apiErrors';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

interface CompanyBillingTabProps {
  companyId: string;
  /** Platform company detail shows status and history only. */
  readOnly?: boolean;
}

export const CompanyBillingTab: React.FC<CompanyBillingTabProps> = ({ companyId, readOnly = false }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [billingAccount, setBillingAccount] = useState<BillingAccount | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadBillingAccount();
    }
  }, [companyId]);

  const loadBillingAccount = async () => {
    setLoading(true);
    try {
      const account = await BillingService.getByCompanyId(companyId);
      setBillingAccount(account);
      setFormInitialized(true);
      if (account) {
        // Use setTimeout to ensure form is mounted
        setTimeout(() => {
          form.setFieldsValue({
            payment_method: account.payment_method || '',
            payment_method_details: account.payment_method_details || '',
            billing_address: account.billing_address || '',
            status: account.status || 'No Billing Account',
          });
        }, 0);
      } else {
        setTimeout(() => {
          form.resetFields();
        }, 0);
      }
    } catch (error) {
      console.error('Error loading billing account:', error);
      message.error(getApiErrorMessage(error, 'Failed to load billing account'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const updateData: BillingAccountUpdate = {
        payment_method: values.payment_method,
        payment_method_details: values.payment_method_details,
        billing_address: values.billing_address,
        status: values.status,
      };

      if (billingAccount) {
        await BillingService.update(companyId, updateData);
        message.success('Billing account updated successfully');
      } else {
        message.warning('Creating a billing account is not available. This API updates an account that already exists.');
        return;
      }
      
      setIsEditing(false);
      await loadBillingAccount();
    } catch (error: any) {
      console.error('Error saving billing account:', error);
      message.error(getApiErrorMessage(error, 'Failed to save billing account'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: BillingStatus): string => {
    switch (status) {
      case 'Active':
        return 'green';
      case 'Past Due':
        return 'orange';
      case 'Suspended':
        return 'red';
      case 'No Billing Account':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: BillingStatus) => {
    switch (status) {
      case 'Active':
        return <CheckCircleOutlined />;
      case 'Past Due':
      case 'Suspended':
        return <ExclamationCircleOutlined />;
      default:
        return <CloseCircleOutlined />;
    }
  };

  if (loading && !billingAccount) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!billingAccount) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <Title level={4}>No Billing Account</Title>
          <Text type="secondary">
            This company has no billing account. This form does not create one, and card capture is not connected.
            Stripe Checkout is a separate flow.
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {billingAccount && !isEditing && (
            <Descriptions title="Billing Account Information" bordered column={1}>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(billingAccount.status)} icon={getStatusIcon(billingAccount.status)}>
                  {billingAccount.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method">
                {billingAccount.payment_method || 'Not set'}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Details">
                {billingAccount.payment_method_details || 'Not set'}
              </Descriptions.Item>
              <Descriptions.Item label="Billing Address">
                {billingAccount.billing_address || 'Not set'}
              </Descriptions.Item>
              <Descriptions.Item label="Last Payment Date">
                {billingAccount.last_payment_date
                  ? new Date(billingAccount.last_payment_date).toLocaleDateString()
                  : 'Never'}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(billingAccount.created_at).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {new Date(billingAccount.updated_at).toLocaleDateString()}
              </Descriptions.Item>
            </Descriptions>
          )}

          {!readOnly && (isEditing || !billingAccount) && formInitialized && (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
            >
              <Form.Item
                name="status"
                label="Billing Status"
                rules={[{ required: true, message: 'Please select a status' }]}
              >
                <Select placeholder="Select billing status">
                  <Option value="Active">Active</Option>
                  <Option value="Past Due">Past Due</Option>
                  <Option value="Suspended">Suspended</Option>
                  <Option value="No Billing Account">No Billing Account</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="payment_method"
                label="Payment Method"
              >
                <Select placeholder="Select payment method" allowClear>
                  <Option value="credit_card">Credit Card</Option>
                  <Option value="bank_transfer">Bank Transfer</Option>
                  <Option value="check">Check</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="payment_method_details"
                label="Payment Method Details"
              >
                <TextArea
                  rows={3}
                  placeholder="Enter payment method details (e.g., card last 4 digits, account number)"
                />
              </Form.Item>

              <Form.Item
                name="billing_address"
                label="Billing Address"
              >
                <TextArea
                  rows={3}
                  placeholder="Enter billing address"
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={loading}
                  >
                    {billingAccount ? 'Update' : 'Create'} Billing Account
                  </Button>
                  {billingAccount && (
                    <Button onClick={() => {
                      setIsEditing(false);
                      loadBillingAccount();
                    }}>
                      Cancel
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Form>
          )}

          {billingAccount && !isEditing && !readOnly && (
            <Button
              type="default"
              onClick={() => setIsEditing(true)}
            >
              Edit Billing Account
            </Button>
          )}
        </Space>
      </Card>
    </div>
  );
};

